import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowDownAZ, BriefcaseBusiness, Plus, Search } from "lucide-react";

import { EmptyState, PageHeader } from "@/components/common/FeatureShell";
import { markContentVisible } from "@/hooks/useNavPerf";
import { NewProjectModal } from "@/components/projects/NewProjectModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSuspenseProjects } from "@/hooks/queries/useProjects";
import { usePrefetchOnIntent } from "@/hooks/usePrefetchOnIntent";
import { type ProjectRow } from "@/lib/api";
import { queryClient } from "@/lib/query";
import { prefetchProjectRouteData } from "@/lib/routeQueries";

type SortKey = "updated" | "name" | "documents";

function ProjectListLink({ project }: { project: ProjectRow }) {
  const prefetchHandlers = usePrefetchOnIntent(() => prefetchProjectRouteData(queryClient, project.id));

  return (
    <Link
      to="/projects/$id"
      params={{ id: project.id }}
      search={{ tab: "assistant" }}
      {...prefetchHandlers}
      className="grid min-h-14 grid-cols-[minmax(0,1fr)_7rem_6rem] items-center border-b px-3 py-2 text-sm last:border-b-0 hover:bg-muted/60"
    >
      <span className="min-w-0">
        <span className="block truncate font-medium">{project.name}</span>
        <span className="block truncate text-xs text-muted-foreground">{project.cm_number || (project.is_owner ? "Owner" : "Shared")}</span>
      </span>
      <span>{project.document_count ?? 0}</span>
      <span>{project.chat_count ?? 0}</span>
    </Link>
  );
}

export function ProjectsPageClient() {
  const { data: projects } = useSuspenseProjects();
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("updated");

  useEffect(() => {
    markContentVisible(!!projects.length);
  }, [projects.length]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return projects
      .filter((project) => !needle || project.name.toLowerCase().includes(needle) || project.cm_number?.toLowerCase().includes(needle))
      .sort((a, b) => {
        if (sort === "name") return a.name.localeCompare(b.name);
        if (sort === "documents") return (b.document_count ?? 0) - (a.document_count ?? 0);
        return Date.parse(b.updated_at ?? b.created_at ?? "") - Date.parse(a.updated_at ?? a.created_at ?? "");
      });
  }, [projects, query, sort]);

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Matters"
        description="Matter workspaces with their own document sets and chats."
        actions={
          <Button type="button" onClick={() => setModalOpen(true)}>
            <Plus className="size-4" />
            New matter
          </Button>
        }
      />
      <main className="p-4 md:p-6">
        <div className="mb-4 flex flex-wrap gap-2">
          <div className="relative min-w-72 flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search matters" className="pl-9" />
          </div>
          <Select value={sort} onValueChange={(value) => setSort(value as SortKey)}>
            <SelectTrigger className="h-10 w-auto min-w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated">Recently updated</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="documents">Documents</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {filtered.length ? (
          <div className="overflow-hidden rounded-md border bg-card">
            <div className="grid grid-cols-[minmax(0,1fr)_7rem_6rem] border-b px-3 py-2 text-xs font-semibold uppercase text-muted-foreground">
              <span>Name</span>
              <span>Docs</span>
              <span>Chats</span>
            </div>
            {filtered.map((project) => (
              <ProjectListLink key={project.id} project={project} />
            ))}
          </div>
        ) : (
          <EmptyState icon={<BriefcaseBusiness className="size-5" />} title="No matters found" description="Create a workspace or adjust the search terms." action={<Button type="button" variant="outline" onClick={() => setModalOpen(true)}><ArrowDownAZ className="size-4" />Create matter</Button>} />
        )}
      </main>
      <NewProjectModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={() => {}} />
    </div>
  );
}
